import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';
import LogoutNavbar from '../../components/LogoutNavbar';
import PostLoginFooter from '../../components/PostLoginFooter';
import Sidebar from '../../components/GlobalSidebar';
import api from '../../api';
import '../../styles/AdminDashboard.css';
import '../../styles/Announcements.css';

function getDefaultHomeForRole(role) {
  if (role === 'club_member') return '/login/clubmember/home';
  if (role === 'admin' || role === 'college_admin') return '/admin/dashboard';
  return '/login/student/home';
}

export default function AnnouncementsPage({ embedded = false } = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready
  const [items, setItems] = useState([]);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [res, me] = await Promise.all([
          api.get('announcements/'),
          api.get('users/me/'),
        ]);

        const list = Array.isArray(res) ? res : (res?.results || []);
        if (!mounted) return;
        setItems(list);
        setUserRole((me?.role || '').toString());
      } catch {
        if (!mounted) return;
        setItems([]);
      } finally {
        if (!mounted) return;
        setStatus('ready');
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const isAdminLayout = userRole === 'admin' || userRole === 'college_admin';

  const formatDateTime = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return '';
    }
  };

  const handleBack = () => {
    try {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number' && idx > 0) {
        navigate(-1);
        return;
      }
    } catch {}
    navigate(getDefaultHomeForRole(userRole), { replace: true });
  };

  const renderHeader = (withBreadcrumbs) => (
    <header className="an-page-header">
      <Box className="an-header-left">
        <Box className="an-breadcrumb-row">
          <Tooltip title="Back">
            <IconButton onClick={handleBack} className="an-back-icon" size="medium" aria-label="Back">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {withBreadcrumbs ? (
            <Breadcrumbs
              className="an-breadcrumbs"
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              <Link
                underline="hover"
                color="inherit"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(getDefaultHomeForRole(userRole), { replace: true });
                }}
                href={getDefaultHomeForRole(userRole)}
                className="an-breadcrumb-link"
              >
                Home
              </Link>
              <Typography color="text.primary" className="an-breadcrumb-current">
                Announcements
              </Typography>
            </Breadcrumbs>
          ) : null}
        </Box>

        <Box className="an-title-wrap">
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="an-page-title">
            Announcements
          </Typography>
          <Typography variant="body2" className="an-page-subtitle" color="text.secondary">
            Updates from your college.
          </Typography>
        </Box>
      </Box>
    </header>
  );

  const renderListCard = () => (
    <Paper className="an-card" elevation={0}>
      <Box className="an-card-header">
        <Box>
          <Typography variant="h6" className="an-card-title">
            All Announcements
          </Typography>
          <Typography className="an-card-subtitle">
            {status === 'loading' ? 'Loading…' : `${items.length} announcement${items.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </Box>

      <Box className="an-card-body">
        {status === 'ready' && items.length === 0 ? (
          <Typography variant="body2">No announcements right now.</Typography>
        ) : (
          <Stack spacing={2}>
            {items.map((a) => {
              const created = formatDateTime(a.created_at);
              const expires = formatDateTime(a.expires_at);

              return (
                <Paper
                  key={a.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: 'rgba(148, 163, 184, 0.55)',
                    background: 'rgba(255,255,255,0.92)',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" className="an-item-title">
                      {a.title}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}>
                      {a.pinned ? <Chip size="small" label="Pinned" /> : null}
                      {a.audience_students ? <Chip size="small" label="Students" variant="outlined" /> : null}
                      {a.audience_club_members ? <Chip size="small" label="Club Members" variant="outlined" /> : null}
                      {a.audience_admins ? <Chip size="small" label="Admins" variant="outlined" /> : null}
                    </Stack>

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {a.message}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} className="an-meta">
                      {created ? <Typography variant="caption">Posted {created}</Typography> : null}
                      {expires ? (
                        <Typography variant="caption">Expires {expires}</Typography>
                      ) : (
                        <Typography variant="caption">No expiry</Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );

  const renderContent = (withBreadcrumbs) => (
    <Stack spacing={2.25}>
      {renderHeader(withBreadcrumbs)}
      {renderListCard()}
    </Stack>
  );

  if (embedded) {
    return (
      <div className="announcements-page" style={{ paddingTop: isMobile ? 8 : 12 }}>
        {renderContent(false)}
      </div>
    );
  }

  if (isAdminLayout) {
    return (
      <div className="admin-dashboard announcements-page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <LogoutNavbar />
        <div className="dashboard-layout" style={{ display: 'flex', flexGrow: 1 }}>
          {!isTabletDown && <Sidebar />}
          <main className="dashboard-main" style={{ flexGrow: 1, overflowX: 'hidden' }}>
            <div className="dashboard-container" style={{ paddingTop: isMobile ? 16 : 24 }}>
              {renderContent(true)}
            </div>
          </main>
        </div>
        <PostLoginFooter />
      </div>
    );
  }

  return (
    <div className="admin-dashboard announcements-page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LogoutNavbar />
      <div className="dashboard-layout" style={{ display: 'flex', flexGrow: 1 }}>
        {!isTabletDown && <Sidebar />}
        <main className="dashboard-main" style={{ flexGrow: 1, overflowX: 'hidden' }}>
          <div className="dashboard-container" style={{ paddingTop: isMobile ? 16 : 24 }}>
            {renderContent(true)}
          </div>
        </main>
      </div>
      {!isMobile && <PostLoginFooter />}
    </div>
  );
}
